'use client'
/***
 *
 * @file components/Navigation/Logo/index.tsx
 * @description NavigationLogo component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 8 March 2024
 *
 */

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
// import "./NavigationLogo.scss";
import Link from "next/link";
import defaultImage from "@/assets/images/defaults/no-logo.png";
import { getDefaultLocale } from "@/config/app.config";

const ImageWrapper = dynamic(() => import("@/components/ImageWrapper"), { ssr: false });

/** determines the type of Props to be passed into the NavigationLogo functional component */
type T_Props = {
  /** CSS classname */
  className?: string,

  /** path of the Logo image */
  src?: string,

  /** alt of the Logo image */
  alt?: string,

  /** data about the Logo, used to set its attributes */
  data?: any,

  /** Event */
  onClick?: () => void; //React.MouseEventHandler<HTMLAnchorElement> | undefined

  /** active locale to preserve when navigating home */
  locale?: string,
}

/**
 * Name: NavigationLogo
 * 
 * Description: a functional component that accepts image data from Contentful and render the image/logo on the UI
 * 
 * @param param0 object containing the following properties:
 *                - className: CSS class to be passed within the Link component containing the logo
 *                - src: source/path of the image to be rendered as a logo
 *                - data: data regarding the logo, served by Contentful
 * @example T_Props
 * ```
 * type T_Props = {
 *     className?: string,        // optional
 *     src?: string,              // optional
 *     data?: any,                // optional
 *     onClick?: React.MouseEventHandler<HTMLAnchorElement> | undefined  //optional
 * }
 * ```
 *                
 * @returns an html element containing the Logo to be displayed on the UI
 */

const NavigationLogo = ({ className = "", src = "", alt="Logo", data = {}, onClick, locale = "" }: T_Props) => {
  const [width, setWidth] = useState(1200);
  const [showButton, setShowButton] = useState(false);

  const homeHref = locale && locale !== getDefaultLocale() ? `/${locale}` : "/";

  const updateWidth = () => {
    const newWidth = window ? window.innerWidth : width;
    setWidth(newWidth);
  };

  useEffect(() => {
    setWidth(1200);
    let ticking = false;

    const updateButtonVisibility = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowButton(window.scrollY >= 400 && window.innerWidth < 768);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", updateButtonVisibility);
    window.addEventListener("resize", updateButtonVisibility);
    window.addEventListener("resize", updateWidth);

    updateWidth();
    updateButtonVisibility();

    return () => {
      window.removeEventListener("scroll", updateButtonVisibility);
      window.removeEventListener("resize", updateButtonVisibility);
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  const logoWidth =
    width < 1024
      ? 140
      : data?.details?.image?.width
        ? parseInt(data.details.image.width)
        : 180;

  const logoHeight =
    width < 1024
      ? 48
      : data?.details?.image?.height
        ? parseInt(data.details.image.height)
        : 58;
  return (
    <>
      <Link
        data-testid="NavigationLogo"
        aria-label="logo"
        href={homeHref}
        prefetch={true}
        onClick={onClick}
        className={`NavigationLogo navbar-brand ${className}`}
        style={{ width: `${logoWidth}px` }}
      >
        <ImageWrapper
          src={src}
          alt={alt || "Logo"}
          defaultImage={defaultImage}
          width={logoWidth}
          height={logoHeight}
          className="relative"
          loader={({ src }) => src}
        />
      </Link>

      {/* Optional: show floating button on mobile scroll */}
      {/* {showButton && (
        <div>
          <Button>btn</Button>
        </div>
      )} */}
    </>
  );
};

export default NavigationLogo;
